import { useMutation, useQueryClient } from '@tanstack/react-query';

import { queryKeysSupplier } from '../queryKeys';

import { type UpdateSupplierResponsiblePersonScopeMutationOptions } from './types';
import { updateSupplierResponsiblePersonScope } from './updateSupplierResponsiblePersonScope';

/**
 * React Query based custom hook for re-scoping a responsible person already
 * attached to a supplier relationship.
 *
 * Invalidates the supplier detail query for the affected data exchange ID on settle.
 *
 * @param options - Optional mutation options.
 * @returns The mutation result.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Suppliers/operation/update_supplier_responsible_person_suppliers__de_id__responsible_persons__user_id__patch
 */
export const useMutationUpdateSupplierResponsiblePersonScope = (
  options?: UpdateSupplierResponsiblePersonScopeMutationOptions,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ deId, payload, userId }) =>
      updateSupplierResponsiblePersonScope(deId, userId, payload),
    onSettled(_data, _error, { deId }) {
      void queryClient.invalidateQueries({
        queryKey: queryKeysSupplier.getDetail(deId),
      });
    },
    throwOnError: false,
    ...options,
  });
};
